import React, { useState, useEffect, useCallback, useMemo } from "react";
import Modal from "newui/components/ui/Modal/Modal";
import {
	SelectWithLabel,
	TextAreaWithLabel,
} from "newui/components/ui/InputWithLabel";
import type { SelectOption } from "newui/components/ui/InputWithLabel";
import { RevisionFilePicker, revisionFileSchema } from "./RevisionFilePicker";
import type {
	EvaluationRevisionSupplier,
	RevisionUploadType,
	UploadRevisionPayload,
} from "../types/revision-review.type";

const MAX_COMMENT_LENGTH = 5000;

interface UploadRevisionModalProps {
	isLoading: boolean;
	isOpen: boolean;
	suppliers: EvaluationRevisionSupplier[];
	onClose: () => void;
	onSubmit: (payload: UploadRevisionPayload) => void;
}

interface FormState {
	comment: string;
	files: File[];
	revisionType: RevisionUploadType;
	rfxInviteId: number | null;
}

const REVISION_TYPE_OPTIONS: { label: string; value: RevisionUploadType; }[] = [
	{ value: "quote", label: "Quote" },
	{ value: "questions", label: "Questions" },
	{ value: "submission_documents", label: "Submission Documents" },
];

const initialFormState: FormState = {
	rfxInviteId: null,
	revisionType: "quote",
	comment: "",
	files: [],
};

export const UploadRevisionModal: React.FC<UploadRevisionModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	isLoading,
	suppliers,
}) => {
	const [form, setForm] = useState<FormState>(initialFormState);
	const [fileError, setFileError] = useState<string>("");
	const [commentError, setCommentError] = useState<string>("");

	useEffect(() => {
		if (isOpen) {
			setForm({
				...initialFormState,
				rfxInviteId: suppliers.length > 0 ? suppliers[0].id : null,
			});
			setFileError("");
			setCommentError("");
		}
	}, [isOpen, suppliers]);

	const handleFilesChange = useCallback((files: File[]) => {
		setForm((prev) => ({ ...prev, files }));
		setFileError("");
	}, []);

	const validateFiles = useCallback(async (files: File[]): Promise<string> => {
		try {
			await revisionFileSchema.validate(files);
			return "";
		} catch (err) {
			if (err instanceof Error) {
				return err.message;
			}
			return "Invalid files";
		}
	}, []);

	const handleSubmit = useCallback(async () => {
		if (!form.rfxInviteId) {
			return;
		}

		if (form.comment.length > MAX_COMMENT_LENGTH) {
			setCommentError(`Comment must be ${MAX_COMMENT_LENGTH} characters or less`);
			return;
		}
		setCommentError("");

		const validationError = await validateFiles(form.files);
		if (validationError) {
			setFileError(validationError);
			return;
		}

		onSubmit({
			rfx_invite_id: form.rfxInviteId,
			revision_type: form.revisionType,
			comment: form.comment || undefined,
			files: form.files,
		});
	}, [form, onSubmit, validateFiles]);

	const supplierOptions: SelectOption[] = useMemo(
		() => suppliers.map((supplier) => ({ label: supplier.name, value: supplier.id })),
		[suppliers]
	);

	const selectedSupplierOption =
		supplierOptions.find((option) => option.value === form.rfxInviteId) ?? null;

	const selectedRevisionTypeOption =
		REVISION_TYPE_OPTIONS.find((option) => option.value === form.revisionType) ?? null;

	const isValid =
		form.rfxInviteId !== null && form.files.length > 0 && !isLoading;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Upload Revision"
			size="lg"
			modalType="standard"
			iconType="create"
			closeOnEsc
			closeOnOverlayClick={false}
			primaryButton={{
				text: isLoading ? "Uploading..." : "Upload Revision",
				onClick: handleSubmit,
				btntype: "primary",
				disabled: !isValid,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: onClose,
			}}
		>
			<div className="space-y-4">
				<SelectWithLabel
					label="Supplier"
					required
					testId="supplier-select"
					options={supplierOptions}
					value={selectedSupplierOption}
					placeholder={
						supplierOptions.length === 0
							? "No suppliers available"
							: "Select a supplier"
					}
					noOptionsMessage={() => "No suppliers available"}
					disabled={supplierOptions.length === 0}
					onChange={(option) =>
						setForm((prev) => ({
							...prev,
							rfxInviteId:
								option && !Array.isArray(option)
									? Number((option as SelectOption).value)
									: null,
						}))
					}
				/>

				<SelectWithLabel
					label="Revision Type"
					required
					testId="revision-type-select"
					options={REVISION_TYPE_OPTIONS}
					value={selectedRevisionTypeOption}
					onChange={(option) => {
						if (option && !Array.isArray(option)) {
							setForm((prev) => ({
								...prev,
								revisionType: (option as SelectOption).value as RevisionUploadType,
							}));
						}
					}}
				/>

				<TextAreaWithLabel
					label="Comment"
					testId="comment-textarea"
					value={form.comment}
					placeholder="Add a comment about this revision..."
					rows={3}
					maxLength={MAX_COMMENT_LENGTH}
					hasError={!!commentError}
					errorText={commentError}
					onChange={(e) => {
						setForm((prev) => ({ ...prev, comment: e.target.value }));
						setCommentError("");
					}}
				/>

				<div>
					<span className="mb-1 block text-sm font-medium text-gray-700">
						Files
					</span>
					<RevisionFilePicker
						files={form.files}
						onChange={handleFilesChange}
						error={fileError}
					/>
				</div>
			</div>
		</Modal>
	);
};
